/**
 * Ports the live-theme Zepto Product Personalizer multiline override.
 *
 * Zepto Standard engraving fields clamp to one line (`maxline` + cstmfy_meta CSV).
 * The browser accepts Enter, then Zepto sanitizes and redraws the canvas from a
 * single line. This raises the line cap, keeps the original character cap
 * (newlines excluded), and replaces Zepto's counter.
 *
 * Source: old theme layout/theme.liquid "Product Personalizer Custom Coding
 * for Character Limit" — reimplemented without jQuery.
 */

const TEXT_SELECTOR = 'textarea.pplr_text';
const COUNTER_CLASS = 'zepto-engraving-counter';
const PARENT_CLASS = 'zepto-engraving-counter-parent';
const LINE_LIMIT = 500;
const LENGTH_OVERRIDE = String(LINE_LIMIT * LINE_LIMIT);
const META_LINE_INDEXES = [7, 17];
const PATCH_DEBOUNCE_MS = 100;

/**
 * @returns {string}
 */
const charactersLeftTemplate = () => {
  return globalThis.Theme?.translations?.zepto_engraving_characters_left || '[remaining] characters left';
};

/**
 * @param {number} remaining
 * @returns {string}
 */
const formatCharactersLeft = (remaining) => {
  return charactersLeftTemplate().replace('[remaining]', String(remaining));
};

/**
 * @param {HTMLTextAreaElement} textarea
 * @returns {number}
 */
const visibleCharacterCount = (textarea) => {
  return textarea.value.replace(/(\r\n|\n|\r)/g, '').trim().length;
};

/**
 * @param {HTMLTextAreaElement} textarea
 */
const hideZeptoCounter = (textarea) => {
  const parent = textarea.parentElement;
  if (!parent) return;

  const counters = parent.querySelectorAll('.pplr-character-count');

  for (const counter of counters) {
    if (!(counter instanceof HTMLElement)) continue;

    counter.hidden = true;
    counter.setAttribute('aria-hidden', 'true');
  }
};

/**
 * @param {string | null} frame
 */
const raisePersonalizerLineLimit = (frame) => {
  if (!frame) return;

  const accessor = `cstmfy_meta_${frame}`;
  const personalizer = window.product_personalizer;
  const config = personalizer?.[accessor];

  if (typeof config !== 'string') return;

  const parts = config.split(',');

  for (const index of META_LINE_INDEXES) {
    if (parts[index] === undefined) continue;

    parts[index] = String(LINE_LIMIT);
  }

  personalizer[accessor] = parts.join(',');
};

/**
 * @param {HTMLTextAreaElement} textarea
 */
const ensureCustomCounter = (textarea) => {
  const parent = textarea.parentElement;
  if (!parent) return;

  parent.classList.add(PARENT_CLASS);

  let counter = parent.querySelector(`.${COUNTER_CLASS}`);

  if (!(counter instanceof HTMLElement)) {
    counter = document.createElement('span');
    counter.className = COUNTER_CLASS;
    counter.id = `${COUNTER_CLASS}-${textarea.getAttribute('data-frame') || String(Date.now())}`;
    parent.append(counter);
  }

  const describedBy = textarea.getAttribute('aria-describedby') || '';
  const tokens = describedBy.split(/\s+/).filter(Boolean);

  if (!tokens.includes(counter.id)) {
    tokens.push(counter.id);
    textarea.setAttribute('aria-describedby', tokens.join(' '));
  }

  const maxLength = Number(textarea.getAttribute('data-default_maxlength'));
  const remaining = Number.isFinite(maxLength) ? Math.max(0, maxLength - visibleCharacterCount(textarea)) : 0;

  counter.dataset.customCount = String(maxLength);
  counter.textContent = formatCharactersLeft(remaining);
};

/**
 * @param {HTMLTextAreaElement} textarea
 */
const patchTextarea = (textarea) => {
  if (textarea.hasAttribute('data-default_maxlength')) {
    hideZeptoCounter(textarea);
    return;
  }

  const originalMaxLength = textarea.getAttribute('data-maxlength') || '';

  textarea.setAttribute('data-default_maxlength', originalMaxLength);
  textarea.setAttribute('data-maxlength', LENGTH_OVERRIDE);
  textarea.setAttribute('maxline', String(LINE_LIMIT));
  textarea.setAttribute('data-prev_value', textarea.value);

  raisePersonalizerLineLimit(textarea.getAttribute('data-frame'));
  hideZeptoCounter(textarea);
  ensureCustomCounter(textarea);
};

const patchTextareas = () => {
  const textareas = document.querySelectorAll(TEXT_SELECTOR);

  for (const textarea of textareas) {
    if (!(textarea instanceof HTMLTextAreaElement)) continue;

    patchTextarea(textarea);
  }
};

/**
 * @param {Event} event
 */
const handleEngravingInput = (event) => {
  const textarea = event.target instanceof Element ? event.target.closest(TEXT_SELECTOR) : null;

  if (!(textarea instanceof HTMLTextAreaElement)) return;

  if (!textarea.hasAttribute('data-default_maxlength')) {
    patchTextarea(textarea);
  }

  const charLimit = Number(textarea.getAttribute('data-default_maxlength'));
  const valueLen = visibleCharacterCount(textarea);

  if (Number.isFinite(charLimit) && valueLen > charLimit) {
    event.stopPropagation();
    textarea.value = textarea.getAttribute('data-prev_value') || '';
    return;
  }

  const parent = textarea.parentElement;
  const counter = parent?.querySelector(`.${COUNTER_CLASS}[data-custom-count], .${COUNTER_CLASS}`);
  const maxLength = Number(counter instanceof HTMLElement ? counter.dataset.customCount : charLimit);

  if (counter instanceof HTMLElement && Number.isFinite(maxLength)) {
    counter.textContent = formatCharactersLeft(Math.max(0, maxLength - valueLen));
  }

  textarea.setAttribute('data-prev_value', textarea.value);
};

let patchTimer = 0;

const schedulePatch = () => {
  window.clearTimeout(patchTimer);
  patchTimer = window.setTimeout(patchTextareas, PATCH_DEBOUNCE_MS);
};

const initZeptoEngravingLimits = () => {
  window.addEventListener('input', handleEngravingInput, true);
  window.addEventListener('keyup', handleEngravingInput, true);
  window.addEventListener('pplrAppInitiated', patchTextareas);

  const observer = new MutationObserver(schedulePatch);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  patchTextareas();
};

if (document.body) {
  initZeptoEngravingLimits();
} else {
  document.addEventListener('DOMContentLoaded', initZeptoEngravingLimits, { once: true });
}
